import type { AIPreferences, AISortPreference } from '@/src/types/aiRecommendation'
import type { SmartSRPErrorCode } from '@/src/utils/srp/aiRecommendation/locale'
import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import type { RootState } from '../rootReducer'

type JourneyLeg = 0 | 1

interface LegAIState {
    loading: boolean
    errorCode: SmartSRPErrorCode | null
    textInput: string
    lastSearchedInput: string | null
    /** Last AI-applied time-range / layover-airport preferences for this leg (for round-tripping). */
    lastAppliedPreferences: AIPreferences | null
    /** Last AI-applied sort intent for this leg (sticky — kept across turns that don't mention sort). */
    lastSortPreference: AISortPreference | null
}

const initialLegState: LegAIState = {
    loading: false,
    errorCode: null,
    textInput: '',
    lastSearchedInput: null,
    lastAppliedPreferences: null,
    lastSortPreference: null
}

interface AIRecommendationState {
    legs: [LegAIState, LegAIState]
}

const initialState: AIRecommendationState = {
    legs: [{ ...initialLegState }, { ...initialLegState }]
}

const aiRecommendationSlice = createSlice({
    name: 'aiRecommendation',
    initialState,
    reducers: {
        setLoading: (state, action: PayloadAction<{ leg: JourneyLeg; value: boolean }>) => {
            const legState = state.legs[action.payload.leg]
            legState.loading = action.payload.value
            if (action.payload.value) legState.errorCode = null
        },
        setErrorCode: (state, action: PayloadAction<{ leg: JourneyLeg; value: SmartSRPErrorCode | null }>) => {
            state.legs[action.payload.leg].errorCode = action.payload.value
        },
        setTextInput: (state, action: PayloadAction<{ leg: JourneyLeg; value: string }>) => {
            state.legs[action.payload.leg].textInput = action.payload.value
        },
        setLastSearchedInput: (state, action: PayloadAction<{ leg: JourneyLeg; value: string }>) => {
            state.legs[action.payload.leg].lastSearchedInput = action.payload.value
        },
        setLastAppliedRecommendation: (
            state,
            action: PayloadAction<{ leg: JourneyLeg; preferences: AIPreferences | null; sortPreference: AISortPreference | null }>
        ) => {
            const legState = state.legs[action.payload.leg]
            // Preferences reflect the full applied state each turn — replace.
            legState.lastAppliedPreferences = action.payload.preferences
            // Sort is sticky: a turn that doesn't mention sort (null) keeps the previous sort intent.
            if (action.payload.sortPreference) legState.lastSortPreference = action.payload.sortPreference
        },
        resetAIState: (state, action: PayloadAction<JourneyLeg>) => {
            state.legs[action.payload] = { ...initialLegState }
        },
        resetAllAIState: state => {
            state.legs = [{ ...initialLegState }, { ...initialLegState }]
        }
    }
})

export const { setLoading, setErrorCode, setTextInput, setLastSearchedInput, setLastAppliedRecommendation, resetAIState, resetAllAIState } = aiRecommendationSlice.actions

const selectLeg = (state: RootState, leg: JourneyLeg): LegAIState =>
    state.aiRecommendation?.legs?.[leg] ?? initialLegState

export const selectAILoading = (state: RootState, leg: JourneyLeg) => selectLeg(state, leg).loading
export const selectAIErrorCode = (state: RootState, leg: JourneyLeg) => selectLeg(state, leg).errorCode
export const selectAITextInput = (state: RootState, leg: JourneyLeg) => selectLeg(state, leg).textInput
export const selectAILastSearchedInput = (state: RootState, leg: JourneyLeg) => selectLeg(state, leg).lastSearchedInput
export const selectHasActiveSmartPreferences = (state: RootState, leg: JourneyLeg) => !!selectLeg(state, leg).lastSearchedInput
export const selectLastAppliedPreferences = (state: RootState, leg: JourneyLeg) => selectLeg(state, leg).lastAppliedPreferences
export const selectLastSortPreference = (state: RootState, leg: JourneyLeg) => selectLeg(state, leg).lastSortPreference

export type { JourneyLeg }
export default aiRecommendationSlice.reducer
